<div class="rsp">

  <!-- Scope-level controls ────────────────────────────────────────── -->
  <!-- Collapsed by default: scope-level fields (currency, minimums,
       percentageDiscount, defaultQuantityBreaks) are infrequently edited.
       The user opens the editor primarily to look at the Rates Preview
       and adjust factor cells; scope settings sit out of the way until
       deliberately opened. Validation errors auto-expand via
       `scopeLevelIssueCount > 0` so problems aren't hidden. -->
  <mat-expansion-panel [expanded]="scopeControlsExpanded || scopeLevelIssueCount > 0" class="rsp-scope-controls"
                       (expandedChange)="onScopeControlsExpandedChange($event)"
                       [class.rce-section-invalid]="scopeLevelIssueCount > 0">
    <mat-expansion-panel-header>
      <mat-panel-title>
        <mat-icon>tune</mat-icon>
        <span i18n="@@Scope settings">Scope settings</span>
        <span class="rsp-scope-name">{{scope}}</span>
        <span class="rce-error-badge" *ngIf="scopeLevelIssueCount > 0"
              [matTooltip]="scopeLevelIssueCount + ' validation error(s) in this section'">
          <mat-icon>error</mat-icon>{{scopeLevelIssueCount}}
        </span>
      </mat-panel-title>
      <mat-panel-description>
        <span class="rsp-spacer"></span>
        <!-- End-side toggle to show/hide the (infrequently-changed) tier list.
             Only meaningful while the scope-settings panel is expanded; we
             stopPropagation so clicking it doesn't collapse the panel. -->
        <button *ngIf="scopeControlsExpanded"
                mat-stroked-button class="rsp-toggle-tiers"
                (click)="toggleTiers($event)"
                [attr.aria-expanded]="tiersExpanded"
                i18n-matTooltip="@@Show or hide the quantity break tiers"
                matTooltip="Show or hide the quantity break tiers">
          <mat-icon>{{tiersExpanded ? 'unfold_less' : 'unfold_more'}}</mat-icon>
          <span i18n="@@Quantity break tiers">Quantity break tiers</span>
        </button>
      </mat-panel-description>
    </mat-expansion-panel-header>

    <div class="rsp-scope-controls-body">

      <mat-form-field appearance="outline" class="rsp-currency">
        <mat-label i18n="@@Currency">Currency</mat-label>
        <mat-select [value]="config.currency || currency" [disabled]="readOnly"
                    (selectionChange)="setCurrency($event.value)">
          <mat-option *ngFor="let c of currencyOptions" [value]="c">{{c}}</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline" class="rsp-discount">
        <mat-label i18n="@@Percentage discount">Percentage discount</mat-label>
        <input matInput type="text" inputmode="decimal"
               [value]="config.percentageDiscount != null ? localizeNumber(config.percentageDiscount) : localizeNumber(0)"
               [disabled]="readOnly"
               (change)="setPercentageDiscountStr($any($event.target).value)"/>
        <span matSuffix>%</span>
        <mat-hint i18n="@@Applied last to unit and per-order cost.">Applied last to unit and per-order cost.</mat-hint>
      </mat-form-field>

      <mat-form-field appearance="outline" class="rsp-min-item">
        <mat-label i18n="@@Minimum per book">Minimum per book</mat-label>
        <input matInput type="text" inputmode="decimal"
               [value]="config.minimums?.perItem != null ? localizeNumber(config.minimums?.perItem) : localizeNumber(0)"
               [disabled]="readOnly"
               (change)="setMinimumPerItemStr($any($event.target).value)"/>
        <mat-hint i18n="@@Floor for the unit cost of each book.">Floor for the unit cost of each book.</mat-hint>
      </mat-form-field>

      <mat-form-field appearance="outline" class="rsp-min-order">
        <mat-label i18n="@@Minimum per order">Minimum per order</mat-label>
        <input matInput type="text" inputmode="decimal"
               [value]="config.minimums?.perOrder != null ? localizeNumber(config.minimums?.perOrder) : localizeNumber(0)"
               [disabled]="readOnly"
               (change)="setMinimumPerOrderStr($any($event.target).value)"/>
      </mat-form-field>

    </div>

    <!-- Quantity break tiers ──────────────────────────────────────── -->
    <div class="rsp-tiers" *ngIf="tiersExpanded">
      <div class="rsp-tiers-header">
        <strong i18n="@@Quantity break tiers">Quantity break tiers</strong>
        <span class="rsp-tiers-hint" i18n="@@Quantity break tiers hint">
          Contiguous integer ranges. The last tier always ends at ∞. Use the + buttons between rows to insert a tier.
        </span>
      </div>
      <table class="rsp-tier-table">
        <thead>
          <tr>
            <th i18n="@@Min quantity">Min quantity</th>
            <th i18n="@@Max quantity">Max quantity</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <ng-container *ngFor="let row of tierRows; let i = index; let last = last; trackBy: trackTierRow">
            <!-- Insert-between affordance: a thin row with a single "+" that
                 inserts a new tier at this position (pushing the current row
                 and everything below it down by one). Hidden for the very
                 first row (use the "below"/append affordance after the last
                 row to get one above the first row… actually we DO show it
                 above the first too — that lets the user prepend, useful when
                 they want a tier starting at e.g. quantity 1). -->
            <tr class="rsp-tier-insert-row">
              <td colspan="3">
                <button mat-icon-button class="rsp-tier-insert-btn"
                        (click)="insertTierRow(i)" [disabled]="readOnly"
                        i18n-matTooltip="@@Insert tier here"
                        matTooltip="Insert tier here"
                        i18n-aria-label="@@Insert tier here" aria-label="Insert tier here">
                  <mat-icon>add</mat-icon>
                </button>
              </td>
            </tr>
            <tr [class.rsp-tier-row-invalid]="tierRowHasError(i)">
              <td>
                <input class="rsp-tier-input" type="text" [value]="row.minStr"
                       [class.rsp-tier-input-invalid]="tierRowHasError(i)"
                       [disabled]="readOnly"
                       (input)="setTierMin(i, $any($event.target).value)"/>
              </td>
              <td>
                <!-- Max is derived: equal to (next row's min - 1), or ∞ for the
                     last tier. Shown read-only so tiers stay contiguous. -->
                <span class="rsp-tier-max">{{last ? '∞' : row.maxStr}}</span>
              </td>
              <td>
                <button mat-icon-button (click)="removeTierRow(i)" [disabled]="readOnly"
                        i18n-aria-label="@@Remove tier" aria-label="Remove tier"
                        i18n-matTooltip="@@Remove tier" matTooltip="Remove tier">
                  <mat-icon>delete</mat-icon>
                </button>
              </td>
            </tr>
          </ng-container>
          <!-- Trailing insert affordance: append a tier after the last row. -->
          <tr class="rsp-tier-insert-row" *ngIf="tierRows.length > 0">
            <td colspan="3">
              <button mat-icon-button class="rsp-tier-insert-btn"
                      (click)="insertTierRow(tierRows.length)" [disabled]="readOnly"
                      i18n-matTooltip="@@Append tier"
                      matTooltip="Append tier"
                      i18n-aria-label="@@Append tier" aria-label="Append tier">
                <mat-icon>add</mat-icon>
              </button>
            </td>
          </tr>
          <tr *ngIf="tierRows.length === 0">
            <td colspan="3" class="rsp-tiers-empty">
              <span i18n="@@No tiers configured — using engine defaults.">No tiers configured — using engine defaults.</span>
              <button mat-stroked-button class="rsp-tiers-seed-btn"
                      (click)="seedDefaultTiers()" [disabled]="readOnly">
                <mat-icon>add</mat-icon>
                <span i18n="@@Add a tier">Add a tier</span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

  </mat-expansion-panel>

  <!-- One section per FactorKind ───────────────────────────────────── -->
  <app-rate-kind-section *ngFor="let kind of visibleFactorKinds"
                         [kind]="kind"
                         [factors]="factorsByKind[kind] || []"
                         [factorAbsoluteIndexes]="factorAbsoluteIndexesByKind[kind] || []"
                         [scopePath]="scopePath"
                         [validation]="validation"
                         [scopeConfig]="config"
                         [readOnly]="readOnly"
                         (addFactor)="addFactor(kind)"
                         (factorChange)="onKindFactorChange(kind, $event.index, $event.factor)"
                         (factorDelete)="onKindFactorDelete(kind, $event)">
  </app-rate-kind-section>

  <!-- Rates preview ─────────────────────────────────────────────────── -->
  <app-rate-preview-matrix [doc]="doc"
                           [baselineDoc]="baselineDoc"
                           [companionDoc]="companionDoc"
                           [companionLabel]="companionLabel"
                           [peerDocs]="peerDocs"
                           [availableFacilities]="availableFacilities"
                           [selectedFacilityIds]="selectedFacilityIds"
                           [showCompanionAsPeer]="showCompanionAsPeer"
                           [isPackingConfig]="isPackingConfig"
                           [scope]="scope"
                           [config]="config"
                           [currency]="config.currency || currency"
                           [readOnly]="readOnly"
                           (pageCountsChange)="onPageCountsChange($event)"
                           (selectedFacilityIdsChange)="selectedFacilityIdsChange.emit($event)"
                           (showCompanionAsPeerChange)="showCompanionAsPeerChange.emit($event)">
  </app-rate-preview-matrix>

</div>



